import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./lib/auth";
import { PersonalizationProvider } from "./lib/personalization";
import { ContentProvider, isPreviewMode } from "./lib/content";
import { MarketingProvider } from "./lib/marketingContent";
import { getTenantSlug } from "./lib/tenantSlug";
import { agentMarketingAllowed } from "./lib/hostnameRouting";
import { ProtectedRoute } from "./components/ProtectedRoute";
import Layout from "./components/Layout";import Landing from "./pages/Landing";
import MarketingLanding from "./pages/MarketingLanding";
import Login from "./pages/Login";
import Register from "./pages/Register";
import AgentSignup from "./pages/AgentSignup";
import BillingSuccess from "./pages/BillingSuccess";
import BillingCancel from "./pages/BillingCancel";
import AdminBillingGate from "./components/AdminBillingGate";
import Dashboard from "./pages/Dashboard";
import BuyerHub from "./pages/BuyerHub";
import PrepGuide from "./pages/PrepGuide";
import Process from "./pages/Process";
import Forms from "./pages/Forms";
import NetSheet from "./pages/NetSheet";
import ImportantDates from "./pages/ImportantDates";
import Activity from "./pages/Activity";
import Vendors from "./pages/Vendors";
import Closing from "./pages/Closing";
import Documents from "./pages/Documents";
import Ask from "./pages/Ask";
import AdminSellers from "./pages/AdminSellers";
import AdminToday from "./pages/AdminToday";
import AdminSellerDetail from "./pages/AdminSellerDetail";
import AdminSubmissions from "./pages/AdminSubmissions";
import AdminMessages from "./pages/AdminMessages";
import AdminContent from "./pages/AdminContent";
import AdminBundles from "./pages/AdminBundles";
import AdminInsights from "./pages/AdminInsights";
import AdminMarketing from "./pages/AdminMarketing";
import AdminBroadcast from "./pages/AdminBroadcast";
import AdminBilling from "./pages/AdminBilling";
import Invite from "./pages/Invite";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import InstallPrompt from "./components/InstallPrompt";
import StageRoute from "./components/StageRoute";

function isMarketingHost() {
  if (typeof window === "undefined") return false;
  const h = window.location.hostname || "";
  // Production marketing domain — root resolves to the agent-facing pitch.
  return h === "theporchlight.io" || h === "www.theporchlight.io";
}

function LandingOrRedirect() {
  const { user, ready } = useAuth();
  if (!ready) return null;
  if (user) {
    // Admin with `?preview=1` — render the unauthenticated seller landing so
    // they can see exactly what their clients see at their portal URL. The
    // share-portal card uses this when the agent clicks "Open" on the preview.
    if (isPreviewMode()) {
      if (isMarketingHost() && !getTenantSlug()) return <MarketingLanding />;
      return <Landing />;
    }
    return <Navigate to={user.role === "admin" ? "/admin" : "/dashboard"} replace />;
  }
  // theporchlight.io root → SaaS marketing site for agents.
  // theporchlight.io/<agent-slug>/ → that agent's seller landing (NOT marketing).
  // Everywhere else (sellwitherika.com, preview, custom agent domains) → seller landing.
  if (isMarketingHost() && !getTenantSlug()) return <MarketingLanding />;
  return <Landing />;
}

function AuthOnly({ children }) {
  const { user, ready } = useAuth();
  if (!ready) return null;
  if (user) {
    // In preview mode, admins want to see exactly what a new seller/agent sees
    // on /login or /register — don't redirect them away. (The iframe thumbnail
    // on /admin uses this same trick.)
    if (isPreviewMode()) return children;
    return <Navigate to={user.role === "admin" ? "/admin" : "/dashboard"} replace />;
  }
  return children;
}

function MarketingOnlyRoute({ children }) {
  // On hosts that belong to a single agent (e.g. sellwitherika.com), agent-
  // facing marketing/signup routes don't apply — quietly route to the
  // seller-side root instead of showing irrelevant content.
  if (!agentMarketingAllowed()) return <Navigate to="/" replace />;
  return children;
}

// Buy-side clients see the BuyerHub tabbed experience on /dashboard.
// Sellers keep the existing dashboard. "Both" clients follow whichever
// side is active — flipping the nav toggle swaps them between the two.
function BuyerAwareDashboard() {
  const { user } = useAuth();
  if (user?.active_side === "buy") return <BuyerHub />;
  return <Dashboard />;
}

function App() {
  const tenantSlug = getTenantSlug();
  return (
    <BrowserRouter basename={tenantSlug ? `/${tenantSlug}` : ""}>
      <AuthProvider>
        <ContentProvider>
          <MarketingProvider>
          <PersonalizationProvider>
            <Layout>
              <Routes>
                <Route path="/" element={<LandingOrRedirect />} />
                <Route path="/for-agents" element={<MarketingOnlyRoute><MarketingLanding /></MarketingOnlyRoute>} />
                <Route path="/signup" element={<MarketingOnlyRoute><AuthOnly><AgentSignup /></AuthOnly></MarketingOnlyRoute>} />
                <Route path="/login" element={<AuthOnly><Login /></AuthOnly>} />
                <Route path="/register" element={<AuthOnly><Register /></AuthOnly>} />
                <Route path="/invite/:token" element={<Invite />} />
                <Route path="/forgot" element={<ForgotPassword />} />
                <Route path="/reset/:token" element={<ResetPassword />} />

                <Route path="/dashboard" element={<ProtectedRoute><BuyerAwareDashboard /></ProtectedRoute>} />
                <Route path="/buyer" element={<ProtectedRoute><BuyerHub /></ProtectedRoute>} />
                <Route path="/prep-guide" element={<ProtectedRoute><StageRoute><PrepGuide /></StageRoute></ProtectedRoute>} />
                <Route path="/process" element={<ProtectedRoute><Process /></ProtectedRoute>} />
                <Route path="/forms" element={<ProtectedRoute><StageRoute><Forms /></StageRoute></ProtectedRoute>} />
                <Route path="/net-sheet" element={<ProtectedRoute><NetSheet /></ProtectedRoute>} />
                <Route path="/dates" element={<ProtectedRoute><ImportantDates /></ProtectedRoute>} />
                <Route path="/activity" element={<ProtectedRoute><StageRoute><Activity /></StageRoute></ProtectedRoute>} />
                <Route path="/vendors" element={<ProtectedRoute><Vendors /></ProtectedRoute>} />
                <Route path="/closing" element={<ProtectedRoute><StageRoute><Closing /></StageRoute></ProtectedRoute>} />
                <Route path="/documents" element={<ProtectedRoute><Documents /></ProtectedRoute>} />
                <Route path="/ask" element={<ProtectedRoute><Ask /></ProtectedRoute>} />

                <Route path="/admin" element={<ProtectedRoute adminOnly><AdminBillingGate><AdminToday /></AdminBillingGate></ProtectedRoute>} />
                <Route path="/admin/clients" element={<ProtectedRoute adminOnly><AdminBillingGate><AdminSellers /></AdminBillingGate></ProtectedRoute>} />
                <Route path="/admin/submissions" element={<ProtectedRoute adminOnly><AdminBillingGate><AdminSubmissions /></AdminBillingGate></ProtectedRoute>} />
                <Route path="/admin/messages" element={<ProtectedRoute adminOnly><AdminBillingGate><AdminMessages /></AdminBillingGate></ProtectedRoute>} />
                <Route path="/admin/bundles" element={<ProtectedRoute adminOnly><AdminBillingGate><AdminBundles /></AdminBillingGate></ProtectedRoute>} />
                <Route path="/admin/content" element={<ProtectedRoute adminOnly><AdminBillingGate><AdminContent /></AdminBillingGate></ProtectedRoute>} />
                <Route path="/admin/seller/:id" element={<ProtectedRoute adminOnly><AdminBillingGate><AdminSellerDetail /></AdminBillingGate></ProtectedRoute>} />
                <Route path="/admin/insights" element={<ProtectedRoute adminOnly><AdminBillingGate><AdminInsights /></AdminBillingGate></ProtectedRoute>} />
                <Route path="/admin/marketing" element={<ProtectedRoute adminOnly><AdminBillingGate><AdminMarketing /></AdminBillingGate></ProtectedRoute>} />
                <Route path="/admin/broadcast" element={<ProtectedRoute adminOnly><AdminBillingGate><AdminBroadcast /></AdminBillingGate></ProtectedRoute>} />
                <Route path="/admin/billing" element={<ProtectedRoute adminOnly><AdminBilling /></ProtectedRoute>} />

                <Route path="/billing/success" element={<ProtectedRoute adminOnly><BillingSuccess /></ProtectedRoute>} />
                <Route path="/billing/cancel" element={<ProtectedRoute adminOnly><BillingCancel /></ProtectedRoute>} />

                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
              <InstallPrompt />
            </Layout>
          </PersonalizationProvider>
          </MarketingProvider>
        </ContentProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
